import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ClipboardList, Search, ShieldCheck } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/")({
  // Signed-in users have nothing to do on a marketing page; the same check
  // `/login` makes, pointed the other way.
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (data) throw redirect({ to: "/agents" });
  },
  component: Home,
});

// One per agent capability, in the words a practice would use for it.
const features = [
  {
    icon: Search,
    title: "Nachschlagen",
    text: "Positionen der GOZ 2012 nach Nummer oder Stichwort finden – mit Punkten und Gebühren zum 1,0-, 2,3- und 3,5-fachen Satz.",
  },
  {
    icon: ShieldCheck,
    title: "Prüfen",
    text: "Klären, welche Leistungen nebeneinander berechnungsfähig sind und welche Paragraphen gelten.",
  },
  {
    icon: ClipboardList,
    title: "Planen",
    text: "Behandlungspläne entwerfen lassen, automatisch prüfen und als PDF-Vorschau ansehen.",
  },
];

function Home() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-12 md:px-8 md:py-20">
      <div className="grid items-center gap-12 md:grid-cols-[1.1fr_1fr]">
        <div className="space-y-6">
          <h1 className="font-serif text-4xl leading-[1.1] tracking-tight md:text-5xl">
            GOZ-Abrechnung,
            <br />
            ohne Nachschlagen.
          </h1>
          <p className="max-w-md text-lg text-muted-foreground leading-relaxed">
            Tycho beantwortet Fragen zur Gebührenordnung für Zahnärzte, berechnet Gebühren und
            entwirft Behandlungspläne.
          </p>
          <Link to="/login" className={buttonVariants({ className: "h-11 px-6 md:h-11" })}>
            Anmelden
          </Link>
        </div>

        {/* A worked example rather than a screenshot: it stays legible in both
            themes and at every width. The figures are the real GOZ 2197 fees
            (130 points × 0,0562421 €), so the page makes no claim the app
            would contradict. */}
        <figure
          aria-label="Beispiel einer Anfrage"
          className="space-y-4 rounded-xl border bg-background p-5"
        >
          <p className="ml-auto w-fit max-w-[85%] rounded-xl bg-muted px-4 py-2.5 text-sm">
            Was kostet GOZ 2197?
          </p>
          <div className="space-y-3 text-sm">
            <p className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs">
              <span aria-hidden className="size-1.5 rounded-full bg-accent" />
              Tycho
            </p>
            <p>
              <strong className="font-semibold">GOZ 2197</strong> – adhäsive Befestigung, 130
              Punkte:
            </p>
            <table className="w-full text-left tabular-nums">
              <tbody className="divide-y">
                {[
                  ["1,0-fach", "7,31 €"],
                  ["2,3-fach (Schwellenwert)", "16,82 €"],
                  ["3,5-fach (Höchstsatz)", "25,59 €"],
                ].map(([factor, amount]) => (
                  <tr key={factor}>
                    <td className="py-1.5 text-muted-foreground">{factor}</td>
                    <td className="py-1.5 text-right font-medium">{amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </figure>
      </div>

      <ul className="mt-20 grid gap-8 md:grid-cols-3">
        {features.map(({ icon: Icon, title, text }) => (
          <li key={title} className="space-y-2">
            <Icon aria-hidden className="size-5 text-accent" />
            <h2 className="font-serif text-xl">{title}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">{text}</p>
          </li>
        ))}
      </ul>

      <p className="mt-20 border-t pt-6 text-muted-foreground text-xs">
        Tycho unterstützt bei der Abrechnung. Die Verantwortung für Rechnungen und Behandlungspläne
        bleibt bei der Praxis.
      </p>
    </div>
  );
}
